import { useMemo, useState, useCallback, useEffect } from "react";
import { Check, X } from "lucide-react";
import {
  formatMDY,
  formatMDYWithTime,
  getStatusColor,
  isFutureRequest,
  isPastWeekRequest,
} from "@utils/ptoHelpers";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi, GridReadyEvent } from "ag-grid-community";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import { AgGridContextMenu } from "@components/ag_grid/AgGridContextMenu";
import { FiCalendar, FiCopy, FiFilter } from "react-icons/fi";

export function PTOTableView({
  requests,
  isLoading,
  approveMutation,
  handleReject,
  cancelMutation,
  searchVal,
  userType,
  onRowDoubleClickDate,
  onRowClickDate,
  showCancelAction = true,
  highlightedRequestId,
}: {
  requests: any[];
  isLoading: boolean;
  approveMutation: any;
  cancelMutation: any;
  handleReject: (id: number) => void;
  searchVal?: string;
  userType?: string;
  onRowDoubleClickDate?: (startDate: string, requestId?: number) => void;
  onRowClickDate?: (startDate: string, requestId?: number) => void;
  showCancelAction?: boolean;
  highlightedRequestId?: number | null;
}) {
  const [gridApi, setGridApi] = useState<GridApi>();
  const [isPendingFiltered, setIsPendingFiltered] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    action: "approve" | "reject" | "cancel" | null;
    request: any;
  }>({ isOpen: false, action: null, request: null });

  // Reset pending filter when requests are re-fetched from the API
  useEffect(() => {
    setIsPendingFiltered(false);
    if (gridApi) {
      const statusFilterInstance = gridApi.getFilterInstance("status");
      if (statusFilterInstance) {
        statusFilterInstance.setModel(null);
        gridApi.onFilterChanged();
      }
    }
  }, [requests, gridApi]);

  const togglePendingFilter = useCallback(() => {
    if (!gridApi) return;
    const statusFilterInstance = gridApi.getFilterInstance("status");
    if (!statusFilterInstance) return;

    if (isPendingFiltered) {
      statusFilterInstance.setModel(null);
      setIsPendingFiltered(false);
    } else {
      statusFilterInstance.setModel({ type: "equals", filter: "Pending" });
      setIsPendingFiltered(true);
    }
    gridApi.onFilterChanged();
  }, [gridApi, isPendingFiltered]);

  // Context menu hook
  const {
    handleCellContextMenu,
    handleCopyFromContextMenu,
    ContextMenuComponent,
  } = AgGridContextMenu({
    menuItems: [
      {
        label: "Copy Cell Value",
        icon: FiCopy,
        onClick: () => handleCopyFromContextMenu(),
        disabled: (value: string) => !value.length,
      },
      {
        label: "Calendar View",
        icon: FiCalendar,
        onClick: (_value, rowData) =>
          onRowDoubleClickDate?.(rowData?.start_date, rowData?.id),
        disabled: () => !onRowDoubleClickDate,
      },
      {
        label: isPendingFiltered ? "Clear Pending Filter" : "Filter Pending",
        icon: FiFilter,
        onClick: () => togglePendingFilter(),
      },
    ],
    // enabledColumns: ["contractor_name", "user_email", "user_id", "description"], // Example of enabling context menu only on specific columns
  });

  // Prevent default browser context menu
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: "Contractor",
        field: "contractor_name",
        filter: true,
        width: 130,
        valueGetter: (params: any) =>
          params.data.contractor_name ||
          params.data.user_email ||
          params.data.user_id,
      },
      {
        headerName: "Contractor Id",
        field: "contractor_id",
        width: 120,
        filter: true,
      },
      {
        headerName: "Requested",
        field: "request_date",
        filter: true,
        width: 130,
        // sort: "desc" as const,
        valueFormatter: (params: any) => formatMDYWithTime(params.value),
      },
      {
        headerName: "Start",
        field: "start_date",
        filter: true,
        width: 100,
        valueFormatter: (params: any) => formatMDY(params.value),
      },
      {
        headerName: "End",
        field: "end_date",
        width: 100,
        filter: true,
        valueFormatter: (params: any) => formatMDY(params.value),
      },
      {
        headerName: "Hrs",
        field: "hours_requested",
        width: 70,
        filter: true,
      },
      {
        headerName: "Bal@Req",
        field: "pto_balance_at_request",
        width: 100,
        filter: true,
        valueFormatter: (params: any) =>
          params.value != null ? params.value : "—",
      },
      {
        headerName: "Bal After",
        field: "pto_balance_after",
        width: 100,
        filter: true,
        valueFormatter: (params: any) =>
          params.value != null ? params.value : "—",
      },
      {
        headerName: "Status",
        field: "status",
        filter: true,
        width: 130,
        cellRenderer: (params: any) => {
          const status = params.value;
          const requestType = params.data.request_type;
          return (
            <div
              className={`tw-capitalize ${getStatusColor(status, requestType)}`}
            >
              {status}
            </div>
          );
        },
      },
      {
        headerName: "Type",
        field: "request_type",
        filter: true,
        width: 100,
        valueFormatter: (params: any) => params.value || "—",
        cellClass: "tw-capitalize",
      },
      {
        headerName: "Temp #",
        field: "temp_number",
        width: 90,
        filter: true,
      },
      {
        headerName: "Description",
        field: "description",
        filter: true,
        width: 200,
        valueFormatter: (params: any) => params.value || "—",
        tooltipField: "description",
      },
      {
        headerName: "Note",
        field: "status_reason",
        filter: true,
        width: 150,
        valueFormatter: (params: any) => params.value || "—",
        tooltipField: "status_reason",
      },
      {
        headerName: "Actions",
        field: "id",
        width: showCancelAction ? 270 : 180,
        pinned: "right",
        cellRenderer: (params: any) => {
          const r = params.data;
          return (
            <div className="tw-flex tw-items-center tw-gap-1">
              <button
                className="tw-inline-flex tw-items-center tw-gap-1 tw-text-green-700 tw-bg-green-100 hover:tw-bg-green-200 tw-border tw-border-green-200 tw-rounded tw-px-2 tw-py-1 disabled:tw-text-gray-500 disabled:tw-bg-gray-100 disabled:tw-border-gray-300 disabled:tw-cursor-not-allowed"
                onClick={() =>
                  setConfirmModal({
                    isOpen: true,
                    action: "approve",
                    request: r,
                  })
                }
                disabled={r.status !== "Pending" || approveMutation.isPending}
                title="Approve"
              >
                <Check size={16} /> Approve
              </button>
              <button
                className="tw-inline-flex tw-items-center tw-gap-1 tw-text-red-700 tw-bg-red-100 hover:tw-bg-red-200 tw-border tw-border-red-200 tw-rounded tw-px-2 tw-py-1 disabled:tw-text-gray-500 disabled:tw-bg-gray-100 disabled:tw-border-gray-300 disabled:tw-cursor-not-allowed"
                onClick={() =>
                  setConfirmModal({
                    isOpen: true,
                    action: "reject",
                    request: r,
                  })
                }
                disabled={r.status !== "Pending"}
                title="Reject"
              >
                <X size={16} /> Reject
              </button>
              {showCancelAction && (
                <button
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-text-orange-700 tw-bg-orange-100 hover:tw-bg-orange-200 tw-border tw-border-orange-200 tw-rounded tw-px-3 tw-py-1.5 disabled:tw-text-gray-500 disabled:tw-bg-gray-100 disabled:tw-border-gray-300 disabled:tw-cursor-not-allowed"
                  onClick={() =>
                    setConfirmModal({
                      isOpen: true,
                      action: "cancel",
                      request: r,
                    })
                  }
                  disabled={
                    !(
                      r.status === "Approved" ||
                      r.status === "Balance_Overridden"
                    ) ||
                    (userType === "super_admin"
                      ? !(isFutureRequest(r) || isPastWeekRequest(r))
                      : !isFutureRequest(r)) ||
                    cancelMutation.isPending
                  }
                >
                  <X size={16} /> Cancel
                </button>
              )}
            </div>
          );
        },
      },
      {
        headerName: "Second Name",
        field: "second_name",
        width: 120,
        filter: true,
      },
      {
        headerName: "Fourth Name",
        field: "fourth_name",
        width: 120,
        filter: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [approveMutation, cancelMutation, userType, handleReject, showCancelAction]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
    }),
    []
  );

  function onGridReady(params: GridReadyEvent): void {
    setGridApi(params.api);
    if (searchVal) {
      params.api.setQuickFilter(searchVal);
    }
    // Highlight row if highlightedRequestId is set
    if (highlightedRequestId != null) {
      setTimeout(() => {
        params.api.deselectAll();
        params.api.forEachNode((node) => {
          if (node.data && node.data.id === highlightedRequestId) {
            node.setSelected(true, true);
            params.api.ensureNodeVisible(node, "middle");
          }
        });
      }, 0);
    }
  }

  // Update quick filter when searchVal changes
  useEffect(() => {
    if (gridApi && searchVal !== undefined) {
      gridApi.setQuickFilter(searchVal);
    }
  }, [gridApi, searchVal]);

  // Highlight row when highlightedRequestId changes
  useEffect(() => {
    if (!gridApi) return;
    gridApi.deselectAll();
    if (highlightedRequestId != null) {
      gridApi.forEachNode((node) => {
        if (node.data && node.data.id === highlightedRequestId) {
          node.setSelected(true, true);
          gridApi.ensureNodeVisible(node, "middle");
        }
      });
    }
  }, [gridApi, highlightedRequestId]);

  const handleConfirmAction = () => {
    if (!confirmModal.request || !confirmModal.action) return;

    const { id } = confirmModal.request;
    switch (confirmModal.action) {
      case "approve":
        approveMutation.mutate({ id });
        break;
      case "reject":
        handleReject(id);
        break;
      case "cancel":
        cancelMutation.mutate({ id });
        break;
    }
    setConfirmModal({ isOpen: false, action: null, request: null });
  };

  if (isLoading) {
    return <div className="tw-p-4 tw-text-gray-500">Loading…</div>;
  }

  if (!requests || requests.length === 0) {
    return <div className="tw-p-4 tw-text-gray-500">No PTO requests yet.</div>;
  }

  return (
    <>
      <div className="tw-bg-white dark:tw-bg-[#0D1117] tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-700">
        <div
          className="ag-theme-balham tw-h-[73dvh] tw-w-full"
          onContextMenu={handleContextMenu}
        >
          <AgGridReact
            columnDefs={columnDefs}
            // rowHeight={40}
            rowData={requests}
            defaultColDef={defaultColDef}
            rowSelection="single"
            onGridReady={onGridReady}
            onCellContextMenu={handleCellContextMenu}
            onRowDoubleClicked={(params) =>
              onRowDoubleClickDate?.(params?.data?.start_date, params?.data?.id)
            }
            onRowClicked={(params) =>
              onRowClickDate?.(params?.data?.start_date, params?.data?.id)
            }
            animateRows
          />
          {ContextMenuComponent}
        </div>
      </div>

      {confirmModal.isOpen && confirmModal.request && (
        <div
          className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-2 tw-bg-black/50"
          onClick={() =>
            setConfirmModal({ isOpen: false, action: null, request: null })
          }
        >
          <div
            className="tw-bg-white dark:tw-bg-[#0D1117] tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-shadow-xl tw-max-w-xs tw-w-full tw-p-3 tw-max-h-[90vh] tw-overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="tw-text-sm tw-font-semibold tw-mb-2">
              Confirm{" "}
              {confirmModal.action
                ? confirmModal.action.charAt(0).toUpperCase() +
                  confirmModal.action.slice(1)
                : ""}
            </h3>
            <p className="tw-text-xs tw-text-gray-600 tw-mb-2">
              Are you sure you want to {confirmModal.action} this PTO request?
            </p>
            <div className="tw-bg-gray-50 dark:tw-bg-gray-800 tw-rounded tw-p-2 tw-mb-2">
              <div className="tw-text-xs sm:tw-text-sm">
                <span className="tw-font-semibold">Contractor: </span>
                {confirmModal.request.contractor_name ||
                  confirmModal.request.user_email ||
                  confirmModal.request.user_id}
              </div>
              <div className="tw-text-xs sm:tw-text-sm tw-text-gray-600 tw-mt-1">
                <span className="tw-font-semibold">Dates: </span>
                {formatMDY(confirmModal.request.start_date)} -{" "}
                {formatMDY(confirmModal.request.end_date)}
              </div>
              <div className="tw-text-xs sm:tw-text-sm tw-text-gray-600">
                <span className="tw-font-semibold">Hours: </span>
                {confirmModal.request.hours_requested}
              </div>
            </div>
            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-justify-end">
              <button
                className="tw-w-full sm:tw-w-auto tw-px-3 tw-py-1 tw-border tw-border-gray-300 tw-rounded tw-text-xs tw-text-gray-700 hover:tw-bg-gray-100"
                onClick={() =>
                  setConfirmModal({
                    isOpen: false,
                    action: null,
                    request: null,
                  })
                }
              >
                Cancel
              </button>
              <button
                className={`tw-w-full sm:tw-w-auto tw-px-3 tw-py-1 tw-rounded tw-text-xs tw-text-white ${
                  confirmModal.action === "approve"
                    ? "tw-bg-green-600 hover:tw-bg-green-700"
                    : confirmModal.action === "reject"
                    ? "tw-bg-red-600 hover:tw-bg-red-700"
                    : "tw-bg-orange-600 hover:tw-bg-orange-700"
                }`}
                onClick={handleConfirmAction}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
